import { useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { ProjectData } from '../types';
import { normalizeUrl } from '../utils/urlUtils';
import { useFileUpload } from '../hooks/useFileUpload';
import { useParams } from 'react-router-dom';
import { X, Upload, Link, Loader2 } from 'lucide-react';
import { HelpTooltip } from './HelpTooltip';

interface UrlFieldProps {
  field: {
    name: string;
    label: string;
    required?: boolean;
    placeholder?: string;
    helperText?: string;
    helpText?: string;
    validation?: {
      pattern?: string;
    };
  };
  readonly?: boolean;
}

export function UrlField({ field, readonly = false }: UrlFieldProps) {
  const { control, setValue, watch } = useFormContext<ProjectData['formData']>();
  const { jiraKey } = useParams<{ jiraKey: string }>();
  const { uploadFile, uploadState, resetUploadState } = useFileUpload();
  
  // State for tracking input type (URL vs File)
  const [inputType, setInputType] = useState<'url' | 'file'>('url');
  const [isDragging, setIsDragging] = useState(false);
  
  // Watch current value
  const currentValue = watch(field.name as keyof ProjectData['formData']);

  const handleFileUpload = async (file: File) => {
    if (!jiraKey) return;
    
    try {
      const downloadURL = await uploadFile(file, jiraKey, field.name);
      setValue(field.name as keyof ProjectData['formData'], downloadURL);
    } catch (error) {
      console.error('File upload failed:', error);
    }
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(false);
    if (readonly) return;

    const file = event.dataTransfer.files?.[0];
    if (file) {
      handleFileUpload(file);
    }
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    if (!readonly) {
      setIsDragging(true);
    }
  };

  const removeFile = () => {
    setValue(field.name as keyof ProjectData['formData'], '');
    resetUploadState();
  };

  return (
    <div className="space-y-2">
      <label htmlFor={field.name} className="block text-sm font-medium text-gray-700">
        {field.label} {field.required && <span className="text-red-500">*</span>}
      </label>
      
      {/* Toggle Buttons */}
      {!readonly && (
        <div className="flex space-x-2 mb-3">
          <button
            type="button"
            onClick={() => setInputType('url')}
            className={`px-3 py-1 text-sm rounded-md flex items-center space-x-1 ${
              inputType === 'url'
                ? 'bg-blue-100 text-blue-700 border border-blue-300'
                : 'bg-gray-100 text-gray-600 border border-gray-300'
            }`}
          >
            <Link className="h-4 w-4" />
            <span>URL</span>
          </button>
          <button
            type="button"
            onClick={() => setInputType('file')}
            className={`px-3 py-1 text-sm rounded-md flex items-center space-x-1 ${
              inputType === 'file'
                ? 'bg-blue-100 text-blue-700 border border-blue-300'
                : 'bg-gray-100 text-gray-600 border border-gray-300'
            }`}
          >
            <Upload className="h-4 w-4" />
            <span>File Upload</span>
          </button>
        </div>
      )}

      {/* URL Input */}
      {inputType === 'url' && (
        <Controller
          name={field.name as keyof ProjectData['formData']}
          control={control}
          rules={{
            required: field.required ? `${field.label} is required` : false,
            pattern: {
              value: new RegExp(field.validation?.pattern || '^(https?://.+|www\\..+|\\w+\\..+|file:.+)'),
              message: 'Please enter a valid URL (e.g., yourcompany.com/terms) or upload a file',
            },
          }}
          render={({ field: controllerField, fieldState: { error } }) => (
            <div>
              <input
                type="url"
                id={field.name}
                placeholder={field.placeholder}
                disabled={readonly}
                value={String(controllerField.value || '')}
                onChange={(e) => {
                  const normalizedUrl = normalizeUrl(e.target.value);
                  if (normalizedUrl !== e.target.value) {
                    controllerField.onChange(normalizedUrl);
                  } else {
                    controllerField.onChange(e);
                  }
                }}
                onBlur={controllerField.onBlur}
                name={controllerField.name}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
              />
              {error && (
                <p className="mt-1 text-sm text-red-600">{error.message}</p>
              )}
            </div>
          )}
        />
      )}

      {/* File Upload */}
      {inputType === 'file' && (
        <div className="space-y-3">
          {!currentValue ? (
            <div
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragEnter={handleDragOver}
              onDragLeave={() => setIsDragging(false)}
              className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
                isDragging
                  ? 'border-blue-400 bg-blue-50'
                  : 'border-gray-300 hover:border-gray-400'
              }`}
            >
              <input
                type="file"
                id={`${field.name}-file`}
                accept=".pdf,.doc,.docx,.txt,.html,.htm"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    handleFileUpload(file);
                  }
                }}
                className="hidden"
                disabled={readonly}
              />
              <label
                htmlFor={`${field.name}-file`}
                className="cursor-pointer flex flex-col items-center space-y-2"
              >
                <Upload className="h-8 w-8 text-gray-400" />
                <span className="text-sm text-gray-600">
                  {isDragging ? 'Drop the file to upload' : 'Click to upload or drag and drop'}
                </span>
                <span className="text-xs text-gray-500">
                  PDF, DOC, DOCX, TXT, HTML (max 10MB)
                </span>
              </label>
            </div>
          ) : (
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div className="flex items-center space-x-2">
                <Upload className="h-5 w-5 text-green-500" />
                <span className="text-sm text-gray-700">
                  {uploadState.uploading ? 'Uploading...' : 'File uploaded successfully'}
                </span>
                {uploadState.uploading && (
                  <Loader2 className="h-4 w-4 text-blue-500 animate-spin" />
                )}
              </div>
              {!readonly && (
                <button
                  type="button"
                  onClick={removeFile}
                  className="text-red-600 hover:text-red-700 flex items-center space-x-1"
                >
                  <X className="h-4 w-4" />
                  <span className="text-sm">Remove</span>
                </button>
              )}
            </div>
          )}
          
          {uploadState.error && (
            <p className="text-sm text-red-600">{uploadState.error}</p>
          )}
        </div>
      )}

      {/* Helper Text */}
      {field.helperText && (
        <p className="text-sm text-gray-500">{field.helperText}</p>
      )}
      
      {/* Help Tooltip */}
      {field.helpText && <HelpTooltip helpText={field.helpText} />}
    </div>
  );
}
